import { useEffect } from 'react';
import { Outlet } from 'react-router';
import { toast } from 'sonner';
import { Sidebar } from '@/components/layout';
import { cn } from '@/lib/utils';
import { Toaster } from '@/components/ui';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { toggleSidebar } from '@/store/slices/uiSlice';
import { AuthGuard } from '@/components/auth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useTenantChangeGuard } from '@/lib/hooks/useTenantChangeGuard';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { CustomerAdminBanner } from '@/components/layout/CustomerAdminBanner';
import { IssueMaintenanceBanner } from '@/components/layout/IssueMaintenanceBanner';
import { useTopBannerLayout } from '@/hooks/useTopBannerLayout';

/**
 * Main application layout with sidebar and content area.
 * Protected by AuthGuard - only accessible when authenticated.
 */
export function AppLayout() {
  const sidebarExpanded = useAppSelector((state) => state.ui.sidebarExpanded);
  const dispatch = useAppDispatch();
  useDocumentTitle();
  useTenantChangeGuard();
  const { updateAvailable, remoteVersion } = useVersionCheck();
  const {
    issueMaintenanceBannerVisible,
    issueMaintenanceAnnouncement,
    dismissIssueMaintenanceBanner,
    topBannerOffsetPx,
    issueMaintenanceBannerTopPx,
  } = useTopBannerLayout();

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  useEffect(() => {
    if (!updateAvailable) return;

    toast.info(`A new version (${remoteVersion?.version}) is available`, {
      description: 'Reload the page to get the latest features and fixes.',
      duration: Infinity,
      action: {
        label: 'Reload',
        onClick: () => window.location.reload(),
      },
    });
  }, [updateAvailable, remoteVersion]);

  return (
    <AuthGuard>
      <CustomerAdminBanner />
      {issueMaintenanceBannerVisible && issueMaintenanceAnnouncement && (
        <IssueMaintenanceBanner
          announcement={issueMaintenanceAnnouncement}
          topOffsetPx={issueMaintenanceBannerTopPx}
          onDismiss={dismissIssueMaintenanceBanner}
        />
      )}
      <div
        className="flex h-screen bg-bg-tertiary"
        style={
          topBannerOffsetPx > 0
            ? {
                height: `calc(100vh - ${topBannerOffsetPx}px)`,
                marginTop: `${topBannerOffsetPx}px`,
              }
            : undefined
        }
      >
        <Sidebar
          expanded={sidebarExpanded}
          onToggle={handleToggleSidebar}
        />
        <main
          className={cn(
            'flex-1 overflow-auto p-2 pt-2',
            sidebarExpanded ? '' : 'pl-0'
          )}
        >
          <div className="bg-bg-primary border border-border-primary rounded-xl h-full overflow-auto">
            <Outlet />
          </div>
        </main>
      </div>
      <Toaster position="top-right" />
    </AuthGuard>
  );
}
